import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
// Question text, options and explanations come back with embedded LaTeX.
// $...$ / $$...$$ are the documented delimiters; \(...\) and \[...\] are
// accepted too because generated content drifts between the two conventions.
export const HAS_MATH = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$\n]+?\$|\\\([\s\S]+?\\\))/;
export const hasMath = (text) => Boolean(text && HAS_MATH.test(String(text)));

const SEGMENT = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$\n]+?\$|\\\([\s\S]+?\\\))/g;
const parseSegment = (token) => {
  if (token.startsWith('$$') && token.endsWith('$$')) {
    return { tex: token.slice(2, -2), display: true };
  }
  if (token.startsWith('\\[') && token.endsWith('\\]')) {
    return { tex: token.slice(2, -2), display: true };
  }
  if (token.startsWith('\\(') && token.endsWith('\\)')) {
    return { tex: token.slice(2, -2), display: false };
  }
  if (token.startsWith('$') && token.endsWith('$')) {
    return { tex: token.slice(1, -1), display: false };
  }
  return null;
};
// **word** marks text to show in bold (the Banking papers use it to point at parts of a sentence).
const BOLD = /\*\*([^*\n]+?)\*\*/g;
const withBold = (prose) =>
  prose.split(BOLD).map((piece, i) => (i % 2 === 1 ? <strong key={i}>{piece}</strong> : piece));

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// The same rendering as <MathText>, as an HTML string, for markup built outside
// React (the PDF export). Prose is escaped; only KaTeX output is raw HTML.
export const mathToHtml = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  if (!text) return '';
  return text
    .split(SEGMENT)
    .filter((part) => part !== undefined && part !== '')
    .map((part) => {
      const segment = parseSegment(part);
      if (!segment) return escapeHtml(part).replace(BOLD, '<strong>$1</strong>').replace(/\n/g, '<br/>');
      try {
        return katex.renderToString(segment.tex, {
          displayMode: segment.display,
          throwOnError: false,
          strict: false
        });
      } catch (err) {
        return escapeHtml(part);
      }
    })
    .join('');
};

// Only KaTeX's own output is ever injected as HTML — the surrounding prose stays
// a React text node, so nothing from the API reaches the DOM unescaped. KaTeX
// runs with its default trust:false, which blocks \href and friends.
const MathText = ({ children, className }) => {
  const text = children === null || children === undefined ? '' : String(children);
  if (!text) return null;
  const parts = text.split(SEGMENT).filter((part) => part !== undefined && part !== '');
  return (
    <span className={className}>
      {parts.map((part, index) => {
        const segment = parseSegment(part);
        if (!segment) return <span key={index}>{withBold(part)}</span>;
        try {
          const html = katex.renderToString(segment.tex, {
            displayMode: segment.display,
            throwOnError: false,
            strict: false
          });
          return (
            <span
              key={index}
              className={segment.display ? 'math-display' : 'math-inline'}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          );
        } catch (err) {
          // A formula KaTeX cannot parse at all falls back to its source text
          // rather than taking the whole question card down.
          console.warn('KaTeX failed to render:', segment.tex, err);
          return <span key={index}>{part}</span>;
        }
      })}
    </span>
  );
};
export default MathText;
